import type { RechercheResultDTO } from '@/features/api/extranet/dto/Demande/Recherche'
import { useAppStateStore } from '@/features/application'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { Dispositif } from '@/features/domaine/types/opco'
import { computed } from 'vue'

export function useDemandeHelper(item: RechercheResultDTO) {
  const appState = useAppStateStore()
  const readonly = computed<boolean>(() => {
    if (item.niveauPermission !== 'Ecriture') return true
    if (appState.contextCourant === TypesContextesAutorisesEnum.Adherent) {
      return ['RepriseAdherent', 'SaisieAdherent'].includes(item.statut) === false
    } else if (appState.contextCourant === TypesContextesAutorisesEnum.Prestataire) {
      return item.statut !== 'SaisiePrestataire'
    }
    return true
  })

  const deletable = computed<boolean>(() => {
    return (
      (readonly.value === false && ['AReprendreAdherent', 'RepriseAdherent'].includes(item.statut) === false) ||
      item.statut === 'RefusAdherent'
    )
  })

  const stagiaires = computed(() => {
    const stagiaires = item?.stagiaires
    if (!stagiaires || stagiaires.length === 0) return ''
    const first = `${stagiaires[0].prenom ?? ''} ${stagiaires[0].nom ?? ''}`.trim()
    const others = stagiaires.length - 1
    return first + (others > 0 ? ` + ${others}` : '')
  })

  const stagiairesList = computed(() => {
    const stagiaires = item?.stagiaires
    if (!stagiaires || stagiaires.length === 0) return []

    return stagiaires
      .map((stagiaire) => {
        return ((stagiaire.prenom ?? '') + ' ' + (stagiaire.nom ?? '')).trim()
      })
      .filter((item) => item !== '')
      .join(', ')
  })

  const prestataires = computed(() => {
    const prestataires = item?.prestataires
    if (!prestataires || prestataires.length === 0) return ''
    const first = `${prestataires[0].denomination ?? ''} `.trim()
    const others = prestataires.length - 1
    return first + (others > 0 ? ` + ${others}` : '')
  })

  const prestatairesList = computed(() => {
    const prestataires = item?.prestataires
    if (!prestataires || prestataires.length === 0) return []

    return prestataires.map((prestataire) => {
      return prestataire.denomination + ' - ' + prestataire.siret.valeur
    })
  })

  const hasWritePermission = computed(() => {
    return item.niveauPermission === 'Ecriture'
  })

  const checkClonePermission = (dispositif: Dispositif) => {
    return appState.getPermissions(dispositif) === 'Ecriture'
  }

  const hasReadPermission = computed(() => {
    return item.niveauPermission === 'Lecture'
  })

  return {
    readonly,
    deletable,
    stagiaires,
    prestataires,
    stagiairesList,
    prestatairesList,
    hasWritePermission,
    hasReadPermission,
    checkClonePermission,
  }
}
